import { useEffect, useMemo, useRef, useState } from 'react'
import SectionHeader from '../../shared/components/ui/SectionHeader'
import TechnologyCard from '../cards/TechnologyCard'
import { API_BASE } from '../../config/api'

interface TechnologyItem {
  name: string
  category?: string
  description?: string
  experience?: string
  level?: string
  icon?: string
}

interface CategoryItem {
  id: string
  label: string
}

export default function TechnologiesSection() {
  const ref = useRef(null)
  const [activeCategory, setActiveCategory] = useState('all')
  const [technologies, setTechnologies] = useState<TechnologyItem[]>([])
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [heroStats, setHeroStats] = useState<Array<{ number: string; label: string }> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const normalizeTech = (raw: unknown): TechnologyItem => {
      const obj = (raw ?? {}) as Record<string, unknown>
      return {
        name: typeof obj.name === 'string' ? obj.name : 'Technology',
        category: typeof obj.category === 'string' ? obj.category : undefined,
        description: typeof obj.description === 'string' ? obj.description : undefined,
        experience: typeof obj.experience === 'string' ? obj.experience : undefined,
        level: typeof obj.level === 'string' ? obj.level : undefined,
        icon: typeof obj.icon === 'string' ? obj.icon : undefined,
      }
    }

    const normalizeHeroStats = (raw: unknown): Array<{ number: string; label: string }> => {
      const root = (raw ?? {}) as Record<string, unknown>
      const data = (root.data ?? root) as Record<string, unknown>
      const statsRaw = data.stats
      const stats = Array.isArray(statsRaw) ? statsRaw : []
      return stats
        .map((s: unknown) => {
          const obj = (s ?? {}) as Record<string, unknown>
          return {
            number: typeof obj.number === 'string' ? obj.number : '',
            label: typeof obj.label === 'string' ? obj.label : '',
          }
        })
        .filter((s) => Boolean(s.number) && Boolean(s.label))
    }

    const fetchTechnologies = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await fetch(`${API_BASE}/api/v1/technologies`)
        if (!res.ok) {
          throw new Error(`Technologies request failed: ${res.status} ${res.statusText}`)
        }
        const data = await res.json()
        const list: unknown[] = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        const normalized: TechnologyItem[] = list.map(normalizeTech)
        if (isMounted) {
          setTechnologies(normalized)
          const categorySet = new Map<string, CategoryItem>()
          normalized.forEach((tech) => {
            if (!tech.category) return
            const id = tech.category
            if (!categorySet.has(id)) {
              const label = id.split('-').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
              categorySet.set(id, { id, label })
            }
          })
          setCategories([{ id: 'all', label: 'All' }, ...Array.from(categorySet.values())])
        }
      } catch (err) {
        if (isMounted) {
          setTechnologies([])
          setCategories([{ id: 'all', label: 'All' }])
          setError(err instanceof Error ? err.message : 'Failed to load technologies')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    const fetchHeroStats = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/content/hero`)
        if (!res.ok) return
        const json = await res.json()
        const normalized = normalizeHeroStats(json)
        if (isMounted) {
          setHeroStats(normalized.length ? normalized : null)
        }
      } catch {
        if (isMounted) {
          setHeroStats(null)
        }
      }
    }

    fetchTechnologies()
    fetchHeroStats()
    return () => {
      isMounted = false
    }
  }, [])

  const filteredTechnologies = useMemo(() => {
    if (activeCategory === 'all') return technologies
    return technologies.filter((tech) => tech.category === activeCategory)
  }, [technologies, activeCategory])

  const stats = useMemo(() => {
    const count = technologies.length

    const findHeroStat = (predicate: (label: string) => boolean) => {
      if (!heroStats?.length) return undefined
      const match = heroStats.find((s) => predicate((s.label ?? '').toLowerCase()))
      return match?.number
    }

    const heroProjects = findHeroStat((l) => l.includes('project'))
    const heroTechnologies = findHeroStat((l) => l.includes('technolog'))
    const heroYears = findHeroStat((l) => l.includes('year') && l.includes('experience')) ?? findHeroStat((l) => l.includes('experience'))

    const projects = heroProjects ?? (count ? `${Math.max(20, count * 3)}+` : '—')
    const technologiesValue = heroTechnologies ?? (count ? `${count}+` : '—')
    const experience = heroYears ?? (count ? `${Math.max(3, Math.round(count / 5))}+` : '—')

    return [
      { label: 'Technologies', value: technologiesValue },
      { label: 'Projects Built', value: projects },
      { label: 'Years Experience', value: experience },
      { label: 'Client Satisfaction', value: '99%' },
    ]
  }, [technologies.length, heroStats])

  return (
    <section id="technologies" ref={ref} className="section-spacing" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="container-page">
        <SectionHeader
          badge="Our Expertise"
          title="Technologies & Tools"
          description="We leverage cutting-edge technologies to build scalable, performant, and innovative solutions for our clients."
        />

        {/* Category Filter & Loading Shimmer Grid */}
        {isLoading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="shimmer-card flex flex-col justify-between p-6" style={{ height: '180px' }}>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg shimmer-item" />
                    <div className="h-4 w-24 rounded shimmer-item" />
                  </div>
                  <div className="space-y-1.5 mb-4">
                    <div className="h-3 w-full rounded shimmer-item" />
                    <div className="h-3 w-4/5 rounded shimmer-item" />
                  </div>
                </div>
                <div className="space-y-2 mt-auto">
                  <div className="h-2.5 w-16 rounded shimmer-item" />
                  <div className="h-1.5 w-full rounded-full shimmer-item" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="text-center py-12" style={{ color: 'var(--muted)' }}>{error}</div>
        )}

        {!isLoading && !error && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => {
              const isActive = activeCategory === category.id
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className="btn-pill"
                  style={{
                    background: isActive ? 'var(--btn-bg)' : 'var(--surface-2)',
                    color: isActive ? 'var(--btn-text)' : 'var(--muted)',
                    border: isActive ? '1px solid var(--btn-bg)' : '1px solid var(--border)',
                    cursor: 'pointer',
                    padding: '8px 18px',
                    fontWeight: 500,
                    fontSize: '0.88rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span style={{ opacity: isActive ? 1 : 0.6 }}>◦</span>
                  {category.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Technologies Grid */}
        {!isLoading && !error && (
          <div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTechnologies.map((technology, index) => (
                <div key={`${technology.name}-${index}`}>
                  <TechnologyCard technology={technology} />
                </div>
              ))}
            </div>
            {!filteredTechnologies.length && (
              <div className="text-center py-16" style={{ color: 'var(--muted)' }}>No technologies found.</div>
            )}
          </div>
        )}

        {/* Stats Section */}
        {!isLoading && (
          <div className="mt-20">
            <div
              className="stats-bar rounded-card overflow-hidden"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
              }}
            >
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className="text-center py-6 px-4"
                  style={{
                    borderRight: i < stats.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                >
                  <div className="stat-number">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}